import { CSSProperties, useEffect, useState } from "react";
import { orderByDirection, orderByFields } from '../../constants';
import { Fan } from 'modules/Types';
import { track } from "analytics";
import useFans from "Hooks/useFans";
import TableElement, { ColDef, RowSelection } from "elements/TableElement";
import Sty from "modules/Sty";
import TagSymphony from "elements/TagSymphony";
import dayjs from "dayjs";
import emptyUser from "assets/images/user.svg";
import Icon from "elements/Icon";
import ButtonSymphony from "elements/ButtonSymphony";
import Loader from "elements/Loader";
import DebugBox from "components/shareable/DebugBox";

const PAGE_SIZE = 25;

interface FansTableProps {
    isMaestro?: boolean,
}

const FansTable = ({
    isMaestro = false,
}: FansTableProps) => {
    const {
        fansMaestroSearchResults,
        fansData,
        fansCount,
        isLoadingFansData,
        getFansData,
        setSelectedFan,
        setShowFansDetailsModal,
        isFirstLoad,
    } = useFans()

    const [pageSize, setPageSize] = useState<number>(PAGE_SIZE)
    const [limit, setLimit] = useState<number>(PAGE_SIZE)
    const [offset, setOffset] = useState<number>(0)
    const [sortByField, setSortByField] = useState<string>(orderByFields.added);
    const [sortDirection, setSortDirection] = useState<string>(orderByDirection.DESC)

    useEffect(() => {
        if (isMaestro || isLoadingFansData) {
            return
        }
        getFansData({
            limit: limit,
            offset: 0,
            orderBy: sortByField,
            direction: sortDirection,
        })
    }, [])

    const onClickFanRow = (fan: Fan) => {
        track("Viewed Fan Details")
        setSelectedFan(fan);
        setShowFansDetailsModal(true);
    }

    const columnDefs: ColDef[] = [
        {
            title: "Email",
            field: "email",
            flex: 1.5,
            isSortable: true,
            renderCell: (row: Fan) => {
                return (
                    <div className="emailTableCell" style={sty.emailTableCell}>
                        <img
                            className="profileImage"
                            src={row.profile_url ?? emptyUser}
                            alt="Profile"
                            style={sty.profileImage}
                        />
                        <div>
                            <div className="emailFan" style={Sty.text14}>{row.email}</div>
                            <div className="emailTableCellContent" style={sty.emailTableCellContent}>
                                <div className="emailFanName" style={Sty.text12grey}>
                                    {row.name}
                                </div>
                                <div className="emailFanTags">
                                    {row.isNew &&
                                        <TagSymphony
                                            className="newTag"
                                            title="New fan!"
                                            type="square"
                                        />
                                    }
                                    {row.newFollower && row.newFollowerPlatformName &&
                                        <TagSymphony
                                            className="newTag"
                                            title={`FOLLOWED YOU ON ${row.newFollowerPlatformName}`}
                                            type="square"
                                        />
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }
        },
        {
            title: "Location",
            field: "city",
            isSortable: true,
            renderCell: (row: Fan) => {
                return (
                    <div className="locationTableCell">
                        {row.city}{row.state && `, ${row.state}`}{row.country && ` (${row.country})`}
                    </div>
                )
            }
        },
        {
            title: "Source",
            field: "source",
            isSortable: true,
            renderCell: (row: Fan) => {
                const {
                    source,
                    presaveFutureReleases,
                } = row;

                if (source && source?.indexOf('brandContentSlug') > -1) {
                    return (
                        <div className="sourceSignedUpTableCell">
                            Signed up through website
                        </div>
                    )
                } else {
                    switch (source) {
                        case "presave":
                            if (presaveFutureReleases) {
                                return (
                                    <div className="sourceForeverSavedTableCell">
                                        <Icon className="sourceIcon" icon="star" />
                                        Forever Saved
                                    </div>
                                )
                            }
                            return (
                                <div className="sourcePreSavedTableCell">
                                    <Icon className="sourceIcon" icon="people" />
                                    Pre-saved
                                </div>
                            )
                        default:
                            return (
                                <div className="sourceUpdatedManuallyTableCell">
                                    Updated manually
                                </div>
                            )
                    }
                }
            },
        },
        {
            title: "Added",
            field: "createdAt", // NOTE: used for sorting even though it's formatted
            isSortable: true,
            isPro: true,
            renderCell: (row: Fan) => {
                return (
                    <div className="createdAtTableCell">
                        {dayjs(row.createdAt).format('M/D/YYYY')}
                    </div>
                )
            }
        },
        {
            title: "Platform",
            field: "platform",
            isSortable: true,
            isPro: true,
            renderCell: (row: Fan) => {
                return (
                    <div className="platformTableCell">
                        {row.platform ?? '–'}
                    </div>
                )
            }
        },
    ]

    if (isMaestro && fansMaestroSearchResults.length === 0) {
        return null
    }

    const rowSelection: RowSelection = {
        mode: "multiRow",
        headerCheckbox: true,
    };

    const handleClickLoadMore = async () => {
        const newLimit = limit + pageSize
        await getFansData({
            limit: newLimit,
            offset: 0,
            // offset: offset + pageSize
        });
        setLimit(newLimit)
        // setOffset(offset + pageSize)
    };

    if (isFirstLoad) {
        return (
            <div
                className="fanbaseComponentLoader"
                style={sty.fanbaseComponentLoader}
            >
                <Loader className="fanbaseComponentLoader" size={40} title="Loading fans..." />
            </div>
        )
    }

    return (
        <>
            <TableElement
                className="fansTableTable"
                rowData={fansData}
                columnDefs={columnDefs}
                rowSelection={rowSelection}
                rowsTitle="fans"
                rowsTotalOverride={fansCount}
                pagination={true}
                paginationPageSize={pageSize}
                paginationPageSizeSelector={[10, 25, 50]}
                onPageSizeChange={(pageSize) => setPageSize(pageSize)}
                onClickRow={onClickFanRow}
                onClickRightArrowAsync={handleClickLoadMore}
            />
            {/* <DebugBox
                alwaysShow
                data={{
                    fansData,
                    fansCount,
                    limit,
                    offset,
                }}
            /> */}
        </>
    )
}

const sty: Record<string, CSSProperties> = {
    emailTableCell: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    emailTableCellContent: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
    },
    profileImage: {
        width: 28,
        height: 28,
        borderRadius: 16,
        border: Sty.border,
    },
    loadFansContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginTop: 24,
        marginRight: 0,
        marginBottom: 0,
        marginLeft: 0
    },
}

export default FansTable;
